import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import * as React from 'react';

import { DISPLAY, FLEX_ALIGN, FLEX_JUSTIFY, SIZE_SCALE } from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

type CheckboxSize = 'small' | 'medium' | 'large';

type CheckboxProps = Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, 'size'> & {
  size?: CheckboxSize;
  indeterminate?: boolean;
};

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  (
    {
      className,
      size = 'medium',
      checked,
      onCheckedChange,
      indeterminate = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const getSizeClasses = (size: CheckboxSize) => {
      switch (size) {
        case 'small':
          return `${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`;
        case 'medium':
          return `${SIZE_SCALE.w5} ${SIZE_SCALE['5']}`;
        case 'large':
          return `${SIZE_SCALE.w6} ${SIZE_SCALE['6']}`;
        default:
          return `${SIZE_SCALE.w5} ${SIZE_SCALE['5']}`;
      }
    };

    const getIconSizeClasses = (size: CheckboxSize) => {
      switch (size) {
        case 'small':
          return `${SIZE_SCALE.w3} ${SIZE_SCALE['3']}`;
        case 'medium':
          return `${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`;
        case 'large':
          return `${SIZE_SCALE.w5} ${SIZE_SCALE['5']}`;
        default:
          return `${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`;
      }
    };

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          'peer shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
          getSizeClasses(size),
          className
        )}
        {...(checked !== undefined && { checked: indeterminate ? 'indeterminate' : checked })}
        {...(onCheckedChange && { onCheckedChange })}
        {...(disabled !== undefined && { disabled })}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={`${DISPLAY.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.center} text-current`}
        >
          {indeterminate ? (
            <Minus className={getIconSizeClasses(size)} />
          ) : (
            <Check className={getIconSizeClasses(size)} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
